import { useCallback, useEffect, useRef, useState } from 'react'
import authStore from '../../store/authStore'
import {
  DEFAULT_VOICE_SEND_CONFIG,
  VoiceSendConfig,
  matchesKeywordSegment,
  stripTrailingKeyword,
  wordCount,
} from './voiceConfig'

const TARGET_SAMPLE_RATE = 16000
const BUFFER_SIZE = 4096

function isVoiceSupported(): boolean {
  if (typeof window === 'undefined') return false
  const hasMedia = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)
  const hasAudioCtx = !!(window.AudioContext || (window as unknown as { webkitAudioContext?: unknown }).webkitAudioContext)
  const secure = window.isSecureContext || window.location.hostname === 'localhost'
  return hasMedia && hasAudioCtx && typeof WebSocket !== 'undefined' && secure
}

function wsBaseUrl(): string {
  const httpBase = process.env.REACT_APP_PYTHON_API_URL || 'http://localhost:8005'
  if (httpBase.startsWith('https')) return httpBase.replace(/^https/, 'wss')
  if (httpBase.startsWith('http')) return httpBase.replace(/^http/, 'ws')
  return httpBase
}

function floatTo16BitPCM(input: Float32Array): ArrayBuffer {
  const buffer = new ArrayBuffer(input.length * 2)
  const view = new DataView(buffer)
  for (let i = 0; i < input.length; i++) {
    const s = Math.max(-1, Math.min(1, input[i]))
    view.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true)
  }
  return buffer
}

function downsample(input: Float32Array, inputRate: number, targetRate: number): Float32Array {
  if (targetRate >= inputRate) return input
  const ratio = inputRate / targetRate
  const newLength = Math.round(input.length / ratio)
  const result = new Float32Array(newLength)
  let offsetResult = 0
  let offsetInput = 0
  while (offsetResult < newLength) {
    const nextOffsetInput = Math.round((offsetResult + 1) * ratio)
    let accum = 0
    let count = 0
    for (let i = offsetInput; i < nextOffsetInput && i < input.length; i++) {
      accum += input[i]
      count++
    }
    result[offsetResult] = count > 0 ? accum / count : 0
    offsetResult++
    offsetInput = nextOffsetInput
  }
  return result
}

interface UseVoiceInputArgs {
  onTranscript: (text: string) => void
  onAutoSend?: (prompt: string) => void
  sendConfig?: VoiceSendConfig
}

interface UseVoiceInputResult {
  isSupported: boolean
  isListening: boolean
  error: string | null
  start: (baseText: string) => void
  stop: () => void
}

export function useVoiceInput({
  onTranscript,
  onAutoSend,
  sendConfig = DEFAULT_VOICE_SEND_CONFIG,
}: UseVoiceInputArgs): UseVoiceInputResult {
  const [isListening, setIsListening] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const wsRef = useRef<WebSocket | null>(null)
  const mediaStreamRef = useRef<MediaStream | null>(null)
  const audioCtxRef = useRef<AudioContext | null>(null)
  const processorRef = useRef<ScriptProcessorNode | null>(null)
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null)
  const baseRef = useRef('')
  const finalizedRef = useRef('')
  const lastEmittedRef = useRef('')
  const onTranscriptRef = useRef(onTranscript)
  const onAutoSendRef = useRef(onAutoSend)
  const configRef = useRef(sendConfig)
  const listeningRef = useRef(false)
  const sentLatchRef = useRef(false)
  const silenceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const isSupported = isVoiceSupported()

  useEffect(() => {
    onTranscriptRef.current = onTranscript
  }, [onTranscript])

  useEffect(() => {
    onAutoSendRef.current = onAutoSend
  }, [onAutoSend])

  useEffect(() => {
    configRef.current = sendConfig
  }, [sendConfig])

  const cleanupAudio = useCallback(() => {
    if (processorRef.current) {
      processorRef.current.disconnect()
      processorRef.current.onaudioprocess = null
      processorRef.current = null
    }
    if (sourceRef.current) {
      sourceRef.current.disconnect()
      sourceRef.current = null
    }
    if (audioCtxRef.current) {
      audioCtxRef.current.close().catch(() => undefined)
      audioCtxRef.current = null
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((t) => t.stop())
      mediaStreamRef.current = null
    }
  }, [])

  const clearSilenceTimer = useCallback(() => {
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current)
      silenceTimerRef.current = null
    }
  }, [])

  const stop = useCallback(() => {
    listeningRef.current = false
    clearSilenceTimer()
    const ws = wsRef.current
    if (ws && ws.readyState === WebSocket.OPEN) {
      try {
        ws.send(JSON.stringify({ action: 'stop' }))
      } catch {
        /* ignore */
      }
      ws.close()
    }
    wsRef.current = null
    cleanupAudio()
    setIsListening(false)
  }, [cleanupAudio, clearSilenceTimer])

  const emit = useCallback((voiceText: string) => {
    const full = baseRef.current + voiceText
    lastEmittedRef.current = full
    onTranscriptRef.current(full)
  }, [])

  const triggerSend = useCallback(
    (prompt: string) => {
      if (sentLatchRef.current) return
      const finalPrompt = prompt.trim()
      if (!finalPrompt || wordCount(finalPrompt) < configRef.current.minWordsToSend) return
      sentLatchRef.current = true
      clearSilenceTimer()
      onAutoSendRef.current?.(finalPrompt)
    },
    [clearSilenceTimer]
  )

  const armSilenceTimer = useCallback(() => {
    const cfg = configRef.current
    clearSilenceTimer()
    if (!cfg.autoSend.enabled) return
    if (sentLatchRef.current) return
    if (!lastEmittedRef.current.trim()) return
    silenceTimerRef.current = setTimeout(() => {
      triggerSend(lastEmittedRef.current)
    }, cfg.autoSend.silenceMs)
  }, [clearSilenceTimer, triggerSend])

  const start = useCallback(
    async (baseText: string) => {
      if (!isSupported || listeningRef.current) return
      setError(null)
      const trimmedBase = baseText.replace(/\s+$/, '')
      baseRef.current = trimmedBase ? `${trimmedBase} ` : ''
      finalizedRef.current = ''
      lastEmittedRef.current = baseRef.current
      sentLatchRef.current = false
      clearSilenceTimer()

      try {
        const token = authStore.token || ''
        const ws = new WebSocket(
          `${wsBaseUrl()}/api/v1/chat/transcribe?token=${encodeURIComponent(token)}`
        )
        ws.binaryType = 'arraybuffer'
        wsRef.current = ws

        ws.onmessage = (event: MessageEvent) => {
          let data: { transcript?: string; is_partial?: boolean; error?: string }
          try {
            data = JSON.parse(event.data)
          } catch {
            return
          }
          if (data.error) {
            setError(data.error)
            stop()
            return
          }
          if (typeof data.transcript !== 'string') return
          const cfg = configRef.current
          if (data.is_partial) {
            emit(finalizedRef.current + data.transcript)
            armSilenceTimer()
            return
          }
          const segment = data.transcript
          if (cfg.keyword.enabled) {
            if (
              cfg.keyword.matchMode === 'own-segment' &&
              matchesKeywordSegment(segment, cfg.keyword.phrases)
            ) {
              triggerSend(baseRef.current + finalizedRef.current)
              return
            }
            if (cfg.keyword.matchMode === 'trailing') {
              const stripped = stripTrailingKeyword(segment, cfg.keyword.phrases)
              if (stripped !== null) {
                finalizedRef.current = `${finalizedRef.current}${stripped} `
                emit(finalizedRef.current)
                triggerSend(baseRef.current + finalizedRef.current)
                return
              }
            }
          }
          finalizedRef.current = `${finalizedRef.current}${segment} `
          emit(finalizedRef.current)
          armSilenceTimer()
        }
        ws.onerror = () => {
          setError('Voice connection error. Please try again.')
        }
        ws.onclose = () => {
          cleanupAudio()
          listeningRef.current = false
          setIsListening(false)
        }

        const stream = await navigator.mediaDevices.getUserMedia({ audio: { channelCount: 1 } })
        mediaStreamRef.current = stream

        const AudioCtx =
          window.AudioContext ||
          (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
        const audioCtx = new AudioCtx()
        audioCtxRef.current = audioCtx
        const source = audioCtx.createMediaStreamSource(stream)
        sourceRef.current = source
        const processor = audioCtx.createScriptProcessor(BUFFER_SIZE, 1, 1)
        processorRef.current = processor
        source.connect(processor)
        processor.connect(audioCtx.destination)
        processor.onaudioprocess = (e: AudioProcessingEvent) => {
          const socket = wsRef.current
          if (!socket || socket.readyState !== WebSocket.OPEN) return
          const input = e.inputBuffer.getChannelData(0)
          const down = downsample(input, audioCtx.sampleRate, TARGET_SAMPLE_RATE)
          socket.send(floatTo16BitPCM(down))
        }

        listeningRef.current = true
        setIsListening(true)
      } catch {
        setError('Microphone access was denied or is unavailable.')
        stop()
      }
    },
    [isSupported, emit, stop, cleanupAudio, triggerSend, armSilenceTimer, clearSilenceTimer]
  )

  useEffect(() => {
    return () => {
      stop()
    }
  }, [stop])

  return { isSupported, isListening, error, start, stop }
}

export default useVoiceInput
